import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, DataTable, Link, Page, Card, Filters, Modal, Text, DropZone, Banner, List, Box, InlineStack, BlockStack, Divider, Button, Spinner, Checkbox } from '@shopify/polaris';
import { formatToCurrency, getTableFooter, fetchTableData, getRequest } from '../../shared/components/functions.jsx';
import { useUrl } from '../../shared/util/hanldesave';
import { applyFilters, addFilters, handleFilterChange, removeFilter, handleFiltersClearAll } from '../../shared/components/listFilter.jsx';
import ExcelExporterModal from '../../shared/components/modals/excelExporterModal.jsx';
import { useDebounce } from '../../shared/components/inputs/useDebounce.js';
import { useLookupStore } from '../../context/useStore.js';
import CompanySelect from '../../shared/components/companySelect.jsx';

const BULK_FILE_SIZE_LIMIT = 5 * 1024 * 1024;

const formatFileSize = (bytes) => {
  if (typeof bytes !== 'number') return '';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / 1024).toFixed(1)} kB`;
};

const normalizeSupplierId = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

export default function ProductsPage() {
  const url = useUrl();
  const navigate = useNavigate();
  const { producttypes, machinebrands, machinetypes } = useLookupStore();

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [filterLoading, setFilterLoading] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkSupplierId, setBulkSupplierId] = useState(null);
  const [bulkSupplier, setBulkSupplier] = useState(null);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploadError, setBulkUploadError] = useState('');
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkSkipDuplicates, setBulkSkipDuplicates] = useState(true); // leave out rows that would create a product that already exists
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [bulkTemplateLoading, setBulkTemplateLoading] = useState(false);
  // optional Dropbox PDF/STEP lookup for the parts of a bulk upsert (background job on the server, polled here)
  const [bulkSearchDropbox, setBulkSearchDropbox] = useState(false);
  const [bulkDropboxJob, setBulkDropboxJob] = useState(null);
  const [bulkDropboxError, setBulkDropboxError] = useState('');
  const bulkDropboxPollRef = useRef(0);
  const [filters, setFilters] = useState({
    q: { type: 'text', name: 'Q', value: url.get('q') || '' },
    code: { type: 'text', name: 'Code', value: url.get('code') || '' },
    status: { type: 'status', name: 'Status', value: url.get('status') || 'all' },
    supplier: { type: 'text', name: 'Supplier', value: url.get('supplier') || '' },
    productRef: { type: 'text', name: 'Product reference', value: url.get('productRef') || '' },
    previousRef: { type: 'text', name: 'Previous reference', value: url.get('previousRef') || '' },
    productName: { type: 'text', name: 'Product name', value: url.get('productName') || '' },
    productGroup: {
      type: 'multiple',
      name: 'Group',
      value: url.get('productGroup') ? JSON.parse(url.get('productGroup')) : null,
      choices: producttypes.map((type) => ({ label: type.OnderdelenType, value: type.OnderdelenTypeID }))
    },
    machineBrand: {
      type: 'multiple',
      name: 'Machine brand',
      value: url.get('machineBrand') ? JSON.parse(url.get('machineBrand')) : null,
      choices: machinebrands.map((brand) => ({ label: brand.merk, value: brand.counter }))
    },
    machineType: {
      type: 'multiple',
      name: 'Machine type',
      value: url.get('machineType') ? JSON.parse(url.get('machineType')) : null,
      choices: machinetypes.map((type) => ({ label: type.Type, value: type.ID }))
    }
  });

  const fetchData = async (abortController) => await fetchTableData(page, '/api/products', setItems, filters, abortController, setFilterLoading);
  const debouncedFetchData = useDebounce(fetchData);

  useEffect(() => {
    const abortController = new AbortController();
    const fetchDataWithAbort = async () => debouncedFetchData(abortController);
    fetchDataWithAbort();
    return () => abortController.abort(); // Cleanup, abort controller when page/filters change
  }, [page, filters]);

  useEffect(() => {
    let isActive = true;
    async function fetchSupplier() {
      const normalized = normalizeSupplierId(bulkSupplierId);
      if (!normalized) {
        if (isActive) setBulkSupplier(null);
        return;
      }
      const data = await getRequest(`/api/companies/${normalized}`);
      if (isActive) setBulkSupplier(data || null);
    }
    fetchSupplier();
    return () => {
      isActive = false;
    };
  }, [bulkSupplierId]);

  const rows = Array.isArray(items)
    ? items.map((item) => {
        const productStatus = item.Discontinued ? <Badge tone="critical">Discontinued</Badge> : <Badge tone="success">Active</Badge>;
        const dateAP = item['Datum bijw_AP'] ? item['Datum bijw_AP'].substring(0, 10) : '';
        const dateVP = item['Datum  bijw_ VP'] ? item['Datum  bijw_ VP'].substring(0, 10) : '';
        const pGroup = producttypes.find((type) => type.OnderdelenTypeID === item.OnderdelenTypeID)?.OnderdelenType || '';
        const pGroupBadge = pGroup ? <Badge>{pGroup}</Badge> : null;
        const machineBrandBadge = machinebrands.find((brand) => brand.counter === item.MachineMerkId)?.merk || '';
        const machineTypeBadge = machinetypes.find((type) => type.ID === item.MachineTypeId)?.Type || '';
        return [
          <Link removeUnderline url={'/products/' + item.ProductID}>
            {item.ProductID}
          </Link>,
          item.Productref,
          item.PreviousReference || '',
          item.ProductNameNL,
          <Link removeUnderline url={`/companies/${item.supplier.ID}`}>
            {item.supplier.name}
          </Link>,
          productStatus,
          pGroupBadge,
          machineBrandBadge ? <Badge>{machineBrandBadge}</Badge> : null,
          machineTypeBadge ? <Badge>{machineTypeBadge}</Badge> : null,
          formatToCurrency(item.Eenheidsaankoopprijs),
          dateAP,
          formatToCurrency(item['Eenheids verkoopprijs']),
          formatToCurrency(item.ServiceFee || 0),
          dateVP
        ];
      })
    : [];

  const handleBulkModalOpen = () => {
    resetBulkState();
    setBulkModalOpen(true);
  };

  const handleBulkModalClose = () => {
    resetBulkState();
    setBulkSupplierId(null);
    setBulkModalOpen(false);
  };

  const resetBulkState = () => {
    setBulkFile(null);
    setBulkUploadError('');
    setBulkResult(null);
    setBulkPreview(null);
    bulkDropboxPollRef.current += 1; // stops polling; the job itself keeps running on the server
    setBulkDropboxJob(null);
    setBulkDropboxError('');
  };

  const startBulkDropboxLookup = async (productIds) => {
    const pollId = ++bulkDropboxPollRef.current;
    setBulkDropboxError('');
    try {
      const response = await fetch('/api/products/dropbox-files-jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productIds })
      });
      let job = await response.json().catch(() => null);
      if (!response.ok || !job?.jobId) throw new Error(job?.msg || 'The Dropbox lookup could not be started.');
      setBulkDropboxJob(job);
      while (!job.isFinished && bulkDropboxPollRef.current === pollId) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        if (bulkDropboxPollRef.current !== pollId) return;
        const pollResponse = await fetch(`/api/products/dropbox-files-jobs/${job.jobId}`);
        const polled = await pollResponse.json().catch(() => null);
        if (!pollResponse.ok || !polled) throw new Error(polled?.msg || 'Lost track of the Dropbox lookup.');
        job = polled;
        if (bulkDropboxPollRef.current === pollId) setBulkDropboxJob(job);
      }
    } catch (error) {
      if (bulkDropboxPollRef.current === pollId) setBulkDropboxError(error.message || 'The Dropbox lookup failed.');
    }
  };

  const handleBulkSupplierChange = (value, _id, label) => {
    const normalized = normalizeSupplierId(value);
    setBulkSupplierId(normalized);
    setBulkPreview(null);
    if (normalized && label) {
      setBulkSupplier((prev) => ({ ...(prev || {}), FIRNR: normalized, NAAM: label }));
    }
    if (!normalized) setBulkSupplier(null);
  };

  const handleBulkFileDrop = (files, acceptedFiles) => {
    if (bulkUploadLoading) return;
    const fileList = acceptedFiles && acceptedFiles.length > 0 ? acceptedFiles : files;
    if (!fileList || fileList.length === 0) return;
    const file = fileList[0];
    if (!file.name?.toLowerCase().match(/\.(xlsx|xls)$/)) {
      setBulkUploadError('Only Excel files (.xlsx, .xls) are supported.');
      setBulkFile(null);
      return;
    }
    if (file.size > BULK_FILE_SIZE_LIMIT) {
      setBulkUploadError('File is larger than 5 MB.');
      setBulkFile(null);
      return;
    }
    setBulkUploadError('');
    setBulkPreview(null);
    setBulkResult(null);
    setBulkFile(file);
  };

  const handleBulkTemplateDownload = async () => {
    try {
      setBulkTemplateLoading(true);
      const response = await fetch('/api/products/bulk-upsert/template');
      if (!response.ok) throw new Error('Failed to download template.');
      const blob = await response.blob();
      const urlObject = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObject;
      a.download = 'products-bulk-template.xlsx';
      a.click();
      window.URL.revokeObjectURL(urlObject);
    } catch (error) {
      setBulkUploadError(error.message || 'Failed to download template.');
    } finally {
      setBulkTemplateLoading(false);
    }
  };

  const bulkSupplierIdReady = normalizeSupplierId(bulkSupplierId);

  // rows of the checked file that would create a product that already exists with the same reference or name
  const bulkPossibleDuplicates = bulkPreview && Array.isArray(bulkResult?.possibleDuplicates) ? bulkResult.possibleDuplicates : [];

  const postBulkUpsert = async (dryRun) => {
    const normalizedSupplierId = bulkSupplierIdReady;
    const formData = new FormData();
    formData.append('supplierId', String(normalizedSupplierId));
    formData.append('file', bulkFile);
    if (dryRun) formData.append('dryRun', 'true');
    const skipDuplicates = !dryRun && bulkSkipDuplicates && bulkPossibleDuplicates.length > 0;
    if (skipDuplicates) formData.append('skipPossibleDuplicates', 'true');

    const response = await fetch(`/api/products/bulk-upsert?supplierId=${normalizedSupplierId}${dryRun ? '&dryRun=true' : ''}${skipDuplicates ? '&skipPossibleDuplicates=true' : ''}`, {
      method: 'POST',
      body: formData
    });
    const payload = await response.json().catch(() => null);
    return { response, payload };
  };

  const validateBulkInputs = () => {
    if (!bulkSupplierIdReady) {
      setBulkUploadError('Please select the supplier for this import.');
      return false;
    }
    if (!bulkFile) {
      setBulkUploadError('Please attach the Excel file to process.');
      return false;
    }
    return true;
  };

  const handleBulkCheck = async () => {
    if (bulkUploadLoading) return;
    if (!validateBulkInputs()) return;

    setBulkUploadError('');
    setBulkResult(null);
    setBulkPreview(null);
    setBulkSkipDuplicates(true);
    setBulkUploadLoading(true);

    try {
      const { response, payload } = await postBulkUpsert(true);

      if (!response.ok) {
        console.error('Bulk upsert preview response not OK:', payload);
        setBulkResult(payload || null);
        const errorMessage = payload?.msg || 'The Excel file could not be checked. Please review it.';
        throw new Error(errorMessage);
      }

      setBulkResult(payload); // shows warnings/skipped rows from the check, without the "completed" banner
      setBulkPreview(payload?.preview || null);
      if (!payload?.preview) throw new Error('The check did not return a preview. Please try again.');
    } catch (error) {
      setBulkUploadError(error.message || 'Bulk upsert check failed.');
    } finally {
      setBulkUploadLoading(false);
    }
  };

  const handleBulkConfirm = async () => {
    if (bulkUploadLoading) return;
    if (!validateBulkInputs()) return;

    setBulkUploadError('');
    setBulkUploadLoading(true);

    try {
      const { response, payload } = await postBulkUpsert(false);

      if (!response.ok) {
        console.error('Bulk upsert response not OK:', payload);
        setBulkResult(payload || null);
        const errorMessage = payload?.msg || 'Bulk upsert failed. Please check the Excel file.';
        throw new Error(errorMessage);
      }

      setBulkPreview(null);
      setBulkResult(payload);
      const processedIds = Array.isArray(payload?.processed) ? payload.processed.map((entry) => entry.productId).filter(Boolean) : [];
      if (bulkSearchDropbox && processedIds.length) startBulkDropboxLookup(processedIds);
      const abortController = new AbortController();
      await fetchTableData(page, '/api/products', setItems, filters, abortController, setFilterLoading);
    } catch (error) {
      setBulkUploadError(error.message || 'Bulk upsert failed.');
    } finally {
      setBulkUploadLoading(false);
    }
  };

  const handleBulkCancelPreview = () => {
    setBulkPreview(null);
    setBulkResult(null);
  };

  const renderBulkPreviewBanner = () => {
    if (!bulkPreview) return null;
    // rows that update an existing part of this supplier (same reference), so the user can see which part would change
    const updateRows = Array.isArray(bulkResult?.previewRows) ? bulkResult.previewRows.filter((row) => row.action === 'update') : [];
    return (
      <Banner tone="info" title="Confirm before anything is saved">
        <BlockStack gap="200">
          <Text as="p">
            You are going to <strong>create {bulkPreview.toCreate}</strong> {bulkPreview.toCreate === 1 ? 'part' : 'parts'}, <strong>update {bulkPreview.toUpdate}</strong>{' '}
            {bulkPreview.toUpdate === 1 ? 'part' : 'parts'} and <strong>skip {bulkPreview.toSkip}</strong> {bulkPreview.toSkip === 1 ? 'row' : 'rows'} (out of {bulkPreview.totalRows} rows in the
            file).
          </Text>
          {updateRows.length > 0 ? (
            <BlockStack gap="100">
              <Text as="p" fontWeight="semibold">
                {updateRows.length === 1 ? 'This row updates a part that already exists' : 'These rows update a part that already exists'} (same reference for {bulkSupplier?.NAAM || 'this supplier'}):
              </Text>
              <div data-testid="bulk-update-rows">
                <List type="bullet">
                  {updateRows.slice(0, 20).map((row) => (
                    <List.Item key={row.rowNumber}>
                      <InlineStack gap="100" blockAlign="center">
                        <Text as="span">
                          Row {row.rowNumber}: {row.productName} ({row.productRef}) →
                        </Text>
                        {row.existingProduct ? (
                          <Link removeUnderline target="_blank" url={`/products/${row.existingProduct.ProductID}`}>
                            {row.existingProduct.ProductNameNL || 'part'} ({row.existingProduct.Productref}) #{row.existingProduct.ProductID}
                          </Link>
                        ) : (
                          <Text as="span" tone="subdued">
                            {row.sameAsRowNumber ? `same reference as row ${row.sameAsRowNumber} in this file` : 'existing part'}
                          </Text>
                        )}
                      </InlineStack>
                    </List.Item>
                  ))}
                </List>
              </div>
              {updateRows.length > 20 ? <Text as="p" variant="bodySm">{`+${updateRows.length - 20} more rows that update an existing part.`}</Text> : null}
            </BlockStack>
          ) : null}
          <Text as="p" variant="bodySm">
            Nothing has been changed in the database yet. Click “Yes, apply changes” to run the upsert, or “No, cancel” to stop.
          </Text>
        </BlockStack>
      </Banner>
    );
  };

  const renderBulkDuplicatesBanner = () => {
    if (!bulkPreview || bulkPossibleDuplicates.length === 0) return null;
    const count = bulkPossibleDuplicates.length;
    return (
      <Banner tone="warning" title={`${count} new ${count === 1 ? 'part already exists' : 'parts already exist'}`}>
        <BlockStack gap="200">
          <Text as="p">
            These rows would create a new part for {bulkSupplier?.NAAM || 'this supplier'}, but a part with the same reference or name already exists. Check that you picked the right supplier and the
            right Excel file.
          </Text>
          <div data-testid="bulk-possible-duplicates">
            <List type="bullet">
              {bulkPossibleDuplicates.slice(0, 10).map((row) => (
                <List.Item key={row.rowNumber}>
                  <BlockStack gap="100">
                    <Text as="span">
                      Row {row.rowNumber}: {row.productName} ({row.productRef}) already exists as
                    </Text>
                    {row.matches.map((match) => (
                      <InlineStack gap="100" blockAlign="center" key={match.ProductID}>
                        <Link removeUnderline target="_blank" url={`/products/${match.ProductID}`}>
                          {match.ProductNameNL} ({match.Productref})
                        </Link>
                        <Text as="span" tone="subdued">
                          {match.supplierName || 'no supplier'}
                        </Text>
                        <Badge>{match.matchedOn.includes('ref') && match.matchedOn.includes('name') ? 'same ref & name' : match.matchedOn.includes('ref') ? 'same ref' : 'same name'}</Badge>
                        {match.Discontinued ? <Badge tone="critical">Discontinued</Badge> : null}
                      </InlineStack>
                    ))}
                  </BlockStack>
                </List.Item>
              ))}
            </List>
          </div>
          {count > 10 ? <Text as="p" variant="bodySm">{`+${count - 10} more rows with an existing part.`}</Text> : null}
          <Checkbox
            label={count === 1 ? "Don't create this part (skip the row)" : `Don't create these ${count} parts (skip the rows)`}
            helpText="The other rows are still created or updated."
            checked={bulkSkipDuplicates}
            onChange={setBulkSkipDuplicates}
            disabled={bulkUploadLoading}
          />
        </BlockStack>
      </Banner>
    );
  };

  const renderBulkSummaryBanner = () => {
    if (!bulkResult?.summary) return null;
    const hasErrors = Array.isArray(bulkResult.errors) && bulkResult.errors.length > 0;
    const tone = hasErrors ? 'warning' : 'success';
    const { summary } = bulkResult;
    return (
      <Banner tone={tone} title="Bulk upsert completed">
        <List type="bullet">
          <List.Item>
            Processed {summary.processedRows}/{summary.totalRows} rows ({summary.created} created · {summary.updated} updated · {summary.skipped} skipped)
          </List.Item>
          <List.Item>Default stockplaats #{summary.defaultStockplaatsId || 1} was used for every newly created product.</List.Item>
        </List>
      </Banner>
    );
  };

  const renderBulkDropboxBanner = () => {
    if (bulkDropboxError) {
      return (
        <Banner tone="critical" title="Dropbox lookup failed">
          <p>{bulkDropboxError}</p>
        </Banner>
      );
    }
    if (!bulkDropboxJob) return null;
    if (!bulkDropboxJob.isFinished) {
      return (
        <Banner tone="info" title="Searching Dropbox for PDF & STEP files">
          <InlineStack gap="200" blockAlign="center">
            <Spinner size="small" />
            <Text as="p">
              {bulkDropboxJob.processed}/{bulkDropboxJob.total} parts checked. You can close this window, the lookup continues in the background.
            </Text>
          </InlineStack>
        </Banner>
      );
    }
    const failed = bulkDropboxJob.failed || [];
    return (
      <Banner tone={failed.length ? 'warning' : 'success'} title="Dropbox lookup finished">
        <BlockStack gap="100">
          <Text as="p">
            {bulkDropboxJob.attachedCount} {bulkDropboxJob.attachedCount === 1 ? 'file' : 'files'} attached to {bulkDropboxJob.productsWithFiles} of {bulkDropboxJob.total} parts.
          </Text>
          {failed.length ? (
            <List type="bullet">
              {failed.slice(0, 5).map((failure, index) => (
                <List.Item key={`${failure.productId}-${index}`}>
                  #{failure.productId}
                  {failure.filename ? ` ${failure.filename}` : ''}: {failure.reason}
                </List.Item>
              ))}
            </List>
          ) : null}
        </BlockStack>
      </Banner>
    );
  };

  const renderBulkResultDetails = () => {
    if (!bulkResult) return null;
    const hasErrors = Array.isArray(bulkResult.errors) && bulkResult.errors.length > 0;
    const hasProcessed = Array.isArray(bulkResult.processed) && bulkResult.processed.length > 0;
    const hasMissingColumns = Array.isArray(bulkResult.missingColumns) && bulkResult.missingColumns.length > 0;
    return (
      <BlockStack gap="200">
        {hasErrors && (
          <Box paddingBlockStart="200">
            <Text as="p" variant="bodyMd" fontWeight="semibold">
              Rows with issues
            </Text>
            <List type="bullet">
              {bulkResult.errors.slice(0, 10).map((error) => (
                <List.Item key={`${error.rowNumber}-${error.message}`}>
                  Row {error.rowNumber}
                  {error.productRef ? ` (${error.productRef})` : ''}: {error.message}
                </List.Item>
              ))}
            </List>
            {bulkResult.errors.length > 10 && (
              <Text as="p" variant="bodySm">
                +{bulkResult.errors.length - 10} more rows reported.
              </Text>
            )}
          </Box>
        )}
        {hasProcessed && (
          <Box paddingBlockStart="200">
            <Text as="p" variant="bodyMd" fontWeight="semibold">
              Recent actions
            </Text>
            <List type="bullet">
              {bulkResult.processed.slice(0, 10).map((entry) => (
                <List.Item key={`${entry.rowNumber}-${entry.productId}`}>
                  Row {entry.rowNumber}: {entry.action === 'created' ? 'Created' : 'Updated'} #{entry.productId} ({entry.productRef})
                </List.Item>
              ))}
            </List>
            {bulkResult.processed.length > 10 && (
              <Text as="p" variant="bodySm">
                Showing 10 of {bulkResult.processed.length} processed rows.
              </Text>
            )}
          </Box>
        )}
        {hasMissingColumns && (
          <Box paddingBlockStart="200">
            <Banner tone="critical" title="Template mismatch">
              <p>Ensure the Excel file keeps the required headers exactly once.</p>
              <List type="bullet">
                {bulkResult.missingColumns.map((column) => (
                  <List.Item key={column}>{column}</List.Item>
                ))}
              </List>
            </Banner>
          </Box>
        )}
        {Array.isArray(bulkResult.detectedColumns) && bulkResult.detectedColumns.length > 0 && (
          <Box paddingBlockStart="200">
            <Text as="p" variant="bodySm">
              Detected columns: {bulkResult.detectedColumns.join(', ')}
            </Text>
          </Box>
        )}
        {Array.isArray(bulkResult.skippedRows) && bulkResult.skippedRows.length > 0 && (
          <Box paddingBlockStart="200">
            <Text as="p" variant="bodyMd" fontWeight="semibold">
              Skipped rows
            </Text>
            <List type="bullet">
              {bulkResult.skippedRows.slice(0, 5).map((row) => (
                <List.Item key={`${row.rowNumber}-skipped`}>
                  Row {row.rowNumber}: {row.reason}
                </List.Item>
              ))}
            </List>
            {bulkResult.skippedRows.length > 5 && (
              <Text as="p" variant="bodySm">
                +{bulkResult.skippedRows.length - 5} additional skipped rows.
              </Text>
            )}
          </Box>
        )}
      </BlockStack>
    );
  };

  const bulkPrimaryDisabled = !bulkSupplierIdReady || !bulkFile || bulkUploadLoading;
  const bulkModalSecondaryActions = bulkPreview
    ? [
        {
          content: 'No, cancel',
          onAction: handleBulkCancelPreview,
          disabled: bulkUploadLoading
        }
      ]
    : [
        {
          content: 'Reset form',
          onAction: resetBulkState,
          disabled: bulkUploadLoading
        }
      ];

  const renderBulkModal = () => (
    <Modal
      open={bulkModalOpen}
      onClose={handleBulkModalClose}
      title="Bulk upsert parts"
      primaryAction={{
        content: bulkUploadLoading ? 'Processing…' : bulkPreview ? 'Yes, apply changes' : 'Check file',
        onAction: bulkPreview ? handleBulkConfirm : handleBulkCheck,
        disabled: bulkPrimaryDisabled,
        loading: bulkUploadLoading
      }}
      secondaryActions={bulkModalSecondaryActions}
    >
      <Modal.Section>
        <BlockStack gap="300">
          <div>
            <Text as="p">
              Upload an Excel file with the exact columns below. We will match rows by supplier + product reference. Existing parts will be updated, and new parts will be created (including their
              default stockplaats line for Kast #1).
            </Text>
            <List type="bullet">
              <List.Item>
                <strong>ProductName</strong> – required, max 60 characters
              </List.Item>
              <List.Item>
                <strong>Productref</strong> – required, max 50 characters
              </List.Item>
              <List.Item>
                <strong>PurchasePrice</strong> – optional, numeric (EUR). Empty or invalid values are ignored.
              </List.Item>
              <List.Item>
                <strong>SalesPrice</strong> – optional, numeric (EUR). Empty or invalid values are ignored.
              </List.Item>
            </List>
          </div>

          <InlineStack gap="200" align="start">
            <Button onClick={handleBulkTemplateDownload} loading={bulkTemplateLoading}>
              Download template (.xlsx)
            </Button>
          </InlineStack>

          <CompanySelect label="Supplier" value={bulkSupplierId} id="BulkSupplier" isClearable isDisabled={bulkUploadLoading} onChange={handleBulkSupplierChange} />
          {bulkSupplier && (
            <Text as="p" variant="bodySm">
              Selected supplier: {bulkSupplier.NAAM}
            </Text>
          )}

          <div>
            <Text as="p" variant="bodySm">
              Excel file
            </Text>
            <DropZone
              allowMultiple={false}
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              onDrop={handleBulkFileDrop}
              disabled={bulkUploadLoading}
            >
              {bulkFile ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 'var(--p-space-400)' }}>
                  <div>
                    <Text as="p" variant="bodyMd">
                      {bulkFile.name}
                    </Text>
                    <Text as="p" variant="bodySm">
                      {formatFileSize(bulkFile.size)}
                    </Text>
                  </div>
                  <Button variant="plain" onClick={() => setBulkFile(null)} disabled={bulkUploadLoading}>
                    Remove
                  </Button>
                </div>
              ) : (
                <DropZone.FileUpload actionHint="Excel (.xlsx or .xls) up to 5 MB" />
              )}
            </DropZone>
          </div>

          {bulkUploadLoading && (
            <InlineStack align="center" gap="200">
              <Spinner size="small" />
              <Text as="p" variant="bodySm">
                Processing rows…
              </Text>
            </InlineStack>
          )}

          {bulkUploadError && (
            <Banner tone="critical" title="Bulk upsert blocked">
              <p>{bulkUploadError}</p>
            </Banner>
          )}

          <Checkbox
            label="Search Dropbox for the PDF & STEP files of these parts"
            helpText="After the upsert, files named exactly like the product name or reference are attached to each created or updated part."
            checked={bulkSearchDropbox}
            onChange={setBulkSearchDropbox}
            disabled={bulkUploadLoading}
          />

          {renderBulkPreviewBanner()}
          {renderBulkDuplicatesBanner()}
          {renderBulkSummaryBanner()}
          {renderBulkResultDetails()}
          {renderBulkDropboxBanner()}

          <Divider />

          <List type="bullet">
            <List.Item>Nothing is saved right away: “Check file” first shows how many parts will be created, updated and skipped, and you decide whether to apply the changes.</List.Item>
            <List.Item>The entire upload runs inside a single transaction; any blocking error cancels every change.</List.Item>
            <List.Item>Every newly created part receives a default stockplaats line for Kast (#1) with quantity 0.</List.Item>
            <List.Item>A summary appears here once the upload finishes.</List.Item>
          </List>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );

  return (
    <>
      {renderBulkModal()}
      <Page title="Products" fullWidth primaryAction={{ content: 'New product', url: '/products/new' }} secondaryActions={[{ content: 'Bulk upsert', onAction: handleBulkModalOpen }]}>
        <Card>
          <Filters
            queryValue={filters.q.value}
            filters={addFilters(filters, setFilters, navigate, setPage)}
            appliedFilters={applyFilters(filters, setFilters, navigate, setPage)}
            loading={filterLoading}
            onQueryChange={(e) => handleFilterChange(e, filters, setFilters, 'q', navigate, setPage)}
            onQueryClear={() => removeFilter('q', filters, setFilters, navigate, setPage)}
            autoComplete="off"
            queryPlaceholder="Filter products name, code and product reference"
            onClearAll={() => handleFiltersClearAll(filters, setFilters, navigate, setPage)}
          >
            <ExcelExporterModal filters={filters} setFilters={setFilters} page={page} setPage={setPage} />
          </Filters>
          <DataTable
            stickyHeader
            increasedTableDensity
            verticalAlign="middle"
            columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
            headings={[
              <b>Code</b>,
              <b>Product reference</b>,
              <b>Prev reference</b>,
              <b>Product name</b>,
              <b>Supplier</b>,
              <b>Status</b>,
              <b>Group</b>,
              <b>Machine brand</b>,
              <b>Machine type</b>,
              <b>Purchase price</b>,
              <b>PP last updated</b>,
              <b>Selling price</b>,
              <b>Service fee</b>,
              <b>SP last updated</b>
            ]}
            rows={rows}
            footerContent={getTableFooter(rows, page, setPage, 'products')}
          />
        </Card>
      </Page>
    </>
  );
}
